import { useRef, useState } from 'react';

import type { PointerEvent, RefObject } from 'react';

// The drag stops this far before the container's right edge, so the handle (and its col-resize
// cursor) never ends up flush against the edge where it would be hard to grab back.
const RESIZE_EDGE_GAP = 16;

/**
 * Drag-to-resize of the pinned name column, like DeviatedLineItemsList. `width` stays null until
 * the user drags, so by default the name column keeps absorbing the free space.
 */
export function useNameColumnResize(scrollRef: RefObject<HTMLDivElement>, minWidth: number) {
  const [width, setWidth] = useState<number | null>(null);
  const startRef = useRef<{ maxWidth: number; width: number; x: number } | null>(null);

  const onPointerDown = (event: PointerEvent<HTMLDivElement>) => {
    // preventDefault stops text selection from starting while dragging across the table.
    event.preventDefault();
    event.currentTarget.setPointerCapture(event.pointerId);
    const nameCell = event.currentTarget.parentElement;
    const row = nameCell?.parentElement;
    const leadingWidth = row?.firstElementChild?.getBoundingClientRect().width ?? 0;
    const actionsWidth = row?.lastElementChild?.getBoundingClientRect().width ?? 0;
    const visibleWidth = scrollRef.current?.clientWidth ?? 0;
    startRef.current = {
      // The pinned block (leading + name + actions) can grow until it fills the visible width.
      maxWidth: Math.max(minWidth, visibleWidth - leadingWidth - actionsWidth - RESIZE_EDGE_GAP),
      width: nameCell?.getBoundingClientRect().width ?? minWidth,
      x: event.clientX
    };
  };

  const onPointerMove = (event: PointerEvent<HTMLDivElement>) => {
    const start = startRef.current;
    if (start) {
      setWidth(Math.min(start.maxWidth, Math.max(minWidth, start.width + event.clientX - start.x)));
    }
  };

  const onPointerUp = () => {
    startRef.current = null;
  };

  return { handleProps: { onPointerDown, onPointerMove, onPointerUp }, width };
}
